import React from 'react';
import { 
  Instagram, Facebook, Loader2, Heart, MessageCircle, 
  TrendingUp, Image, Video, ExternalLink, Users, 
  Eye, BarChart3, Calendar, Share2, Bookmark
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ClientReports({ clientId, businessId }) {
  const [loading, setLoading] = React.useState(true);
  const [reportData, setReportData] = React.useState(null);

  React.useEffect(function loadData() {
    if (!clientId && !businessId) {
      setLoading(false);
      return;
    }

    let url = API_URL + '/api/reports/client/' + (clientId || 'unknown') + '/current';
    if (businessId) {
      url += '?business_id=' + businessId;
    }

    fetch(url)
      .then(function(res) { return res.json(); })
      .then(function(data) { 
        setReportData(data); 
      })
      .catch(function(err) { console.error(err); })
      .finally(function() { setLoading(false); });
  }, [clientId, businessId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        <span className="ml-3 text-[#AEAEB2]">Cargando reportes...</span>
      </div>
    );
  }

  if (!clientId && !businessId) {
    return (
      <div className="bg-white/40 rounded-2xl border border-white/80 p-8 text-center">
        <Instagram className="w-16 h-16 text-[#6E6E73] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">Reportes no disponibles</h3>
        <p className="text-[#AEAEB2]">
          No se pudo identificar tu cuenta. Por favor, vuelve a seleccionar tu negocio.
        </p>
      </div>
    );
  }

  const hasMeta = reportData && reportData.has_meta;
  if (!hasMeta) {
    const msg = (reportData && reportData.message) || 'Tu negocio aún no tiene Instagram/Facebook vinculado.';
    return (
      <div className="bg-white/40 rounded-2xl border border-white/80 p-8 text-center">
        <Facebook className="w-16 h-16 text-[#6E6E73] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">Reportes no disponibles</h3>
        <p className="text-[#AEAEB2]">{msg}</p>
      </div>
    );
  }

  // Extract data
  const igData = reportData.instagram || {};
  const igMetrics = igData.metrics || {};
  const igUsername = reportData.ig_username || '';
  const topPosts = reportData.top_posts || [];
  const recentPosts = reportData.recent_posts || [];
  const fbData = reportData.facebook || {};

  // Get metrics
  const followers = igMetrics.followers_count || igMetrics.followers || 0;
  const posts = igMetrics.posts || igData.posts_count || 0;
  const following = igMetrics.following || 0;
  const reach = igMetrics.reach || 0;
  const impressions = igMetrics.impressions || 0;

  // Calculate engagement
  const totalLikes = recentPosts.reduce((sum, p) => sum + (p.likes || 0), 0);
  const totalComments = recentPosts.reduce((sum, p) => sum + (p.comments || 0), 0);
  const avgEngagement = recentPosts.length > 0 
    ? ((totalLikes + totalComments) / recentPosts.length).toFixed(1) 
    : 0;
  const engagementRate = followers > 0 
    ? ((totalLikes + totalComments) / (recentPosts.length * followers) * 100).toFixed(2)
    : 0;

  function formatNum(n) {
    if (n === null || n === undefined) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  }

  // Simple bar chart component
  function SimpleBarChart({ data, maxValue }) {
    return (
      <div className="flex items-end gap-1 h-16">
        {data.map((value, idx) => {
          const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
          return (
            <div 
              key={idx}
              className="flex-1 bg-gradient-to-t from-purple-600 to-pink-500 rounded-t transition-all hover:from-purple-500 hover:to-pink-400"
              style={{ height: `${Math.max(height, 5)}%` }}
              title={`${value} interacciones`}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-2xl flex items-center justify-center shadow-lg">
            <Instagram className="w-7 h-7 text-[#1D1D1F]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#1D1D1F]">Reportes de Redes Sociales</h2>
            {igUsername && (
              <a 
                href={`https://instagram.com/${igUsername}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-pink-400 hover:text-pink-300 flex items-center gap-1 text-sm"
              >
                @{igUsername}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <div className="text-sm text-[#AEAEB2] flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Actualizado: {new Date().toLocaleDateString('es-MX')}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 rounded-xl p-4 border border-purple-700/30">
          <div className="flex items-center gap-2 text-purple-300 mb-2">
            <Users className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">Seguidores</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]">{formatNum(followers)}</p>
        </div>
        
        <div className="bg-gradient-to-br from-pink-900/40 to-pink-800/20 rounded-xl p-4 border border-pink-700/30">
          <div className="flex items-center gap-2 text-pink-300 mb-2">
            <Image className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">Publicaciones</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]">{formatNum(posts)}</p>
        </div>
        
        <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 rounded-xl p-4 border border-orange-700/30">
          <div className="flex items-center gap-2 text-orange-300 mb-2">
            <Heart className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">Engagement Prom.</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]">{avgEngagement}</p>
          <p className="text-xs text-orange-300">likes + comments por post</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 rounded-xl p-4 border border-blue-700/30">
          <div className="flex items-center gap-2 text-blue-300 mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">Tasa Engagement</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]">{engagementRate}%</p>
          <p className="text-xs text-blue-300">interacciones / seguidores</p>
        </div>
      </div>

      {/* Engagement Chart */}
      {recentPosts.length > 0 && (
        <div className="bg-white/40 rounded-xl p-5 border border-white/80">
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            Engagement por Publicación (Últimas {recentPosts.length})
          </h3>
          <SimpleBarChart 
            data={recentPosts.map(p => (p.likes || 0) + (p.comments || 0))}
            maxValue={Math.max(...recentPosts.map(p => (p.likes || 0) + (p.comments || 0)))}
          />
          <div className="flex justify-between mt-2 text-xs text-[#AEAEB2]">
            <span>Más antiguo</span>
            <span>Más reciente</span>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/80">
            <div className="text-center">
              <p className="text-2xl font-bold text-pink-400">{formatNum(totalLikes)}</p>
              <p className="text-xs text-[#AEAEB2]">Total Likes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{formatNum(totalComments)}</p>
              <p className="text-xs text-[#AEAEB2]">Total Comentarios</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">{formatNum(totalLikes + totalComments)}</p>
              <p className="text-xs text-[#AEAEB2]">Total Interacciones</p>
            </div>
          </div>
        </div>
      )}

      {/* Top Posts */}
      {topPosts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Top {topPosts.length} Publicaciones (Mayor Engagement)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topPosts.map((post, idx) => (
              <a
                key={post.id || idx}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/40 rounded-xl overflow-hidden border border-white/80 hover:border-pink-500/50 transition-all hover:shadow-lg hover:shadow-pink-500/10"
              >
                <div className="aspect-square relative">
                  {post.thumbnail ? (
                    <img 
                      src={post.thumbnail} 
                      alt="" 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-white/70 flex items-center justify-center">
                      {post.type === 'VIDEO' || post.type === 'REEL' ? (
                        <Video className="w-12 h-12 text-[#6E6E73]" />
                      ) : (
                        <Image className="w-12 h-12 text-[#6E6E73]" />
                      )}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg">
                    #{idx + 1}
                  </div>
                  {(post.type === 'VIDEO' || post.type === 'REEL' || post.type === 'CAROUSEL_ALBUM') && (
                    <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                      {post.type === 'REEL' ? '🎬 Reel' : post.type === 'VIDEO' ? '🎬 Video' : '📷 Carrusel'}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-4 text-sm mb-2">
                    <span className="flex items-center gap-1.5 text-pink-400">
                      <Heart className="w-4 h-4 fill-current" />
                      <span className="font-semibold">{formatNum(post.likes)}</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-blue-400">
                      <MessageCircle className="w-4 h-4" />
                      <span className="font-semibold">{formatNum(post.comments)}</span>
                    </span>
                  </div>
                  {post.caption && (
                    <p className="text-[#AEAEB2] text-sm line-clamp-2">{post.caption}</p>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Recent Posts Gallery */}
      {recentPosts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-blue-400" />
            Publicaciones Recientes
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {recentPosts.map((post, idx) => (
              <a
                key={post.id || idx}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-square rounded-lg overflow-hidden bg-white/70"
              >
                {post.thumbnail ? (
                  <img 
                    src={post.thumbnail} 
                    alt="" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-[#6E6E73]" />
                  </div>
                )}
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                  <span className="flex items-center gap-1 text-[#1D1D1F] text-xs">
                    <Heart className="w-3 h-3 fill-current text-pink-400" />
                    {post.likes}
                  </span>
                  <span className="flex items-center gap-1 text-[#1D1D1F] text-xs">
                    <MessageCircle className="w-3 h-3 text-blue-400" />
                    {post.comments}
                  </span>
                </div>
                {/* Type badge */}
                {(post.type === 'VIDEO' || post.type === 'REEL' || post.type === 'CAROUSEL_ALBUM') && (
                  <div className="absolute top-1 right-1">
                    {post.type === 'CAROUSEL_ALBUM' ? (
                      <Bookmark className="w-4 h-4 text-[#1D1D1F] drop-shadow-lg" />
                    ) : (
                      <Video className="w-4 h-4 text-[#1D1D1F] drop-shadow-lg" />
                    )}
                  </div>
                )}
                {/* Date */}
                {post.timestamp && (
                  <div className="absolute bottom-1 left-1 bg-black/70 px-1.5 py-0.5 rounded text-[10px] text-[#6E6E73]">
                    {formatDate(post.timestamp)}
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Facebook Section */}
      {Object.keys(fbData).length > 0 && (
        <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-800/30">
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <Facebook className="w-5 h-5 text-blue-500" />
            Facebook
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {fbData.page_fans && (
              <div className="bg-black/20 rounded-lg p-3">
                <p className="text-2xl font-bold text-[#1D1D1F]">{formatNum(fbData.page_fans)}</p>
                <p className="text-xs text-blue-300">Fans de página</p>
              </div>
            )}
            {fbData.page_impressions && (
              <div className="bg-black/20 rounded-lg p-3">
                <p className="text-2xl font-bold text-[#1D1D1F]">{formatNum(fbData.page_impressions)}</p>
                <p className="text-xs text-blue-300">Impresiones</p>
              </div>
            )}
            {fbData.page_engaged_users && (
              <div className="bg-black/20 rounded-lg p-3">
                <p className="text-2xl font-bold text-[#1D1D1F]">{formatNum(fbData.page_engaged_users)}</p>
                <p className="text-xs text-blue-300">Usuarios engaged</p>
              </div>
            )}
            {fbData.page_reach && (
              <div className="bg-black/20 rounded-lg p-3">
                <p className="text-2xl font-bold text-[#1D1D1F]">{formatNum(fbData.page_reach)}</p>
                <p className="text-xs text-blue-300">Alcance</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Note about data source */}
      {reportData.note && (
        <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-4">
          <p className="text-amber-200 text-sm">
            <strong>ℹ️ Nota:</strong> {reportData.note}
          </p>
        </div>
      )}

      {/* Info about Facebook */}
      {Object.keys(fbData).length === 0 && (
        <div className="bg-white/50 border border-[rgba(0,0,0,0.06)] rounded-lg p-4">
          <p className="text-[#AEAEB2] text-sm flex items-center gap-2">
            <Facebook className="w-4 h-4 text-blue-400" />
            <span>
              <strong>Facebook:</strong> Para ver métricas de Facebook, es necesario vincular una Página de Facebook desde el panel de administración.
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
